import React, { useEffect } from 'react';
import { nanoid } from 'nanoid';

import { AccordionItem } from './AccordionItem';

type AccordionItemType = {
  title: string;
  content: React.ReactElement;
};

interface Props {
  items: AccordionItemType[];
}

export const Accordion = ({ items }: Props) => {
  const accordion = React.useRef<HTMLDivElement>(null);
  const [openedIndex, setOpenedIndex] = React.useState<number>();
  const [maxHeight, setMaxHeight] = React.useState(0);
  const [width, setWidth] = React.useState(0);
  const [defaultItemContent, setDefaultItemContent] = React.useState(0);
  const [accordionStartHeight, setAccordionStartHeight] = React.useState(0);

  const updateMaxHeight = (newMaxHeight: number) => {
    if (width < 1024) {
      setMaxHeight(newMaxHeight);

      return;
    }

    setMaxHeight((current: number) => (newMaxHeight > current ? newMaxHeight : current));
  };

  const recountAccordionStartHeight = (widthInner: number) => {
    setAccordionStartHeight(0);
    setWidth(widthInner);
  };

  useEffect(() => {
    recountAccordionStartHeight(window.innerWidth);
    window.addEventListener('resize', () => recountAccordionStartHeight(window.innerWidth));

    return () => window.removeEventListener('resize', () => recountAccordionStartHeight(window.innerWidth));
  }, []);

  useEffect(() => {
    if (accordion.current && accordionStartHeight === 0) {
      setAccordionStartHeight(accordion.current.scrollHeight);
    }
  }, [accordionStartHeight, defaultItemContent, maxHeight]);

  return (
    <div className="accordion" ref={accordion}>
      {items.map(({ title, content }, index) => {
        const isOpen = openedIndex === index;
        const lessIndexClicked = !!openedIndex && openedIndex > index;

        return (
          <AccordionItem
            key={nanoid()}
            lesserIndex={lessIndexClicked}
            title={title}
            isOpen={isOpen}
            content={content}
            onClick={() => setOpenedIndex(isOpen ? -1 : index)}
            updateMaxHeight={updateMaxHeight}
            updateOpenedHeight={setDefaultItemContent}
          />
        );
      })}
    </div>
  );
};
